import {parseNumber} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_13, FONT_PROXIMA_14} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import {BLUE_LIGHT, GRAY_LIGHT, GRAY_LIGHTER, RED, WHITE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import TextField from 'ui-library/inputs/TextField';
import {InputChangeEvent} from 'util/Types';

import {SharedMonomerValidationErrors} from '../SharedMonomerConstants';

export interface MonomerStructureAndWeightConfigurationPaneProps {
  smilesInput: string;
  customMolecularWeight: string | null;
  calculatedMolecularWeight: number | null;
  onChangeCustomMolecularWeight: (weight: string | null) => void;
  validationErrors: SharedMonomerValidationErrors;
  editingMolecularWeight: boolean;
  onChangeEditingMolecularWeight: (editing: boolean) => void;
  structureImage: string | null;
  hasCustomWeightError: boolean;
  onChangeHasCustomWeightError: (isValid: boolean) => void;
  // When true, the molecular weight cannot be edited and the "Edit" affordance is hidden.
  readOnly?: boolean;
}

export default function MonomerStructureAndWeightConfigurationPane({
  smilesInput,
  customMolecularWeight,
  onChangeCustomMolecularWeight,
  calculatedMolecularWeight,
  validationErrors,
  editingMolecularWeight,
  onChangeEditingMolecularWeight,
  structureImage,
  hasCustomWeightError,
  onChangeHasCustomWeightError,
  readOnly = false,
}: MonomerStructureAndWeightConfigurationPaneProps): JSX.Element {
  const updateHasCustomWeightError = (newCustomMolecularWeight: string | null): void => {
    if (newCustomMolecularWeight == null) {
      onChangeHasCustomWeightError(false);
    } else if (validationErrors.molecularWeight != null) {
      onChangeHasCustomWeightError(true);
    } else {
      const numericValue = parseNumber(newCustomMolecularWeight);
      onChangeHasCustomWeightError(numericValue == null || numericValue < 0);
    }
  };

  const molecularWeightSection = (
    <div className={css(styles.molecularWeightSection)}>
      <span>Molecular weight: </span>
      <div className={css(styles.molecularWeightInputRightSection)}>
        <TextField
          className={css(styles.molecularWeightInput)}
          onChange={(e: InputChangeEvent) => {
            onChangeCustomMolecularWeight(e.target.value);
            updateHasCustomWeightError(e.target.value);
          }}
          value={customMolecularWeight ?? calculatedMolecularWeight?.toFixed(3) ?? ''}
          hasError={hasCustomWeightError}
          disabled={!editingMolecularWeight}
          dataTestAttrs={{
            component: 'MonomerStructureAndWeightConfigurationPane',
            element: 'weightField',
          }}
        />
        {editingMolecularWeight ? (
          <div className={css(styles.molecularWeightButtons)}>
            <Tooltip title="Reset to calculated weight">
              <BenchlingIcon
                className={css(styles.iconButtonCursor)}
                icon="close"
                onClick={() => {
                  onChangeCustomMolecularWeight(null);
                  updateHasCustomWeightError(null);
                  onChangeEditingMolecularWeight(false);
                }}
              />
            </Tooltip>
            <Tooltip title="Save custom weight">
              <BenchlingIcon
                className={css(styles.iconButtonBlueLight)}
                icon="check"
                onClick={() => {
                  onChangeEditingMolecularWeight(false);
                }}
                dataTestAttrs={{
                  component: 'MonomerStructureAndWeightConfigurationPane',
                  element: 'saveWeightButton',
                }}
              />
            </Tooltip>
          </div>
        ) : (
          !readOnly && (
            <div>
              <span
                className={css(styles.editMolecularWeight)}
                onClick={() => {
                  onChangeEditingMolecularWeight(true);
                }}
                {...getTestProps({
                  component: 'MonomerStructureAndWeightConfigurationPane',
                  element: 'editWeightButton',
                })}
              >
                Edit
              </span>
            </div>
          )
        )}
      </div>
    </div>
  );

  return (
    <div className={css(styles.chemicalStructurePaneOuter)}>
      <div className={css(styles.chemicalStructurePaneInner)}>
        <div className={css(styles.chemicalStructureHeader)}>
          <span>Chemical structure</span>
        </div>
        <div className={css(styles.bottomBorder)} />
        {molecularWeightSection}
        {smilesInput !== '' ? (
          <div className={css(styles.chemicalStructureContainer)}>
            {structureImage != null && (
              <img
                className={css(styles.chemicalStructureSvg)}
                src={`data:image/svg+xml;base64,${structureImage}`}
              />
            )}
            {validationErrors.structureImage != null && <div>{validationErrors.structureImage}</div>}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  chemicalStructurePaneOuter: {
    width: '50%',
    display: 'flex',
    flexDirection: 'column',
    background: GRAY_LIGHTER,
    padding: 8,
  },
  chemicalStructurePaneInner: {
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0px 2px 4px rgba(92, 124, 131, 0.2)',
    background: WHITE,
    borderRadius: 4,
    height: '100%',
  },
  chemicalStructureContainer: {
    flex: 1,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: RED,
  },
  chemicalStructureSvg: {
    height: '70%',
    width: '70%',
  },
  chemicalStructureHeader: {
    flex: '0 0 auto',
    ...FONT_PROXIMA_14,
    fontWeight: 'bold',
    margin: 12,
  },
  bottomBorder: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    width: '100%',
  },
  molecularWeightSection: {
    flex: '0 0 auto',
    marginLeft: 12,
    marginTop: 8,
    display: 'flex',
    gap: 12,
    flexDirection: 'row',
    alignItems: 'center',
    ...FONT_PROXIMA_14,
  },
  molecularWeightInputRightSection: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
  },
  molecularWeightButtons: {
    display: 'flex',
    gap: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  editMolecularWeight: {
    ...FONT_PROXIMA_13,
    color: BLUE_LIGHT,
    cursor: 'pointer',
  },
  iconButtonCursor: {
    cursor: 'pointer',
    width: 10,
    height: 10,
  },
  iconButtonBlueLight: {
    cursor: 'pointer',
    color: BLUE_LIGHT,
  },
  molecularWeightInput: {
    maxWidth: 75,
    display: 'flex',
    justifyContent: 'center',
    ...FONT_PROXIMA_13,
  },
});
